import {
  INSIDER_CART_CHANGED,
  silentCartUpdate,
} from './mod-cart-event-bus.js';
import type { CartChangedDetail } from './mod-cart-event-bus.js';

// Guard distinctUntilChanged — descarta evento se o token não mudou.
// O cart.token é fixo por sessão no Shopify, então, na prática, só persiste uma vez.
let _lastPersistedToken: string | null = null;
let _currentToken = '';
let _lastPersistedStableKey: string | null = null;

function readStableId(): string | null {
  try {
    return localStorage.getItem('STATSIG_LOCAL_STORAGE_STABLE_ID') || null;
  } catch {
    return null;
  }
}

async function persistStableId(): Promise<void> {
  const stableID = readStableId();
  if (!stableID) return;

  // Inclui o token para persistir novamente se o Shopify trocar o carrinho.
  const key = JSON.stringify([_currentToken, stableID]);
  if (key === _lastPersistedStableKey) return;
  _lastPersistedStableKey = key;

  try {
    await silentCartUpdate({ attributes: { stableID } });
  } catch (err) {
    if (_lastPersistedStableKey === key) _lastPersistedStableKey = null;
    console.warn('[cart-attribute-tracker] Failed to persist stableID:', err);
  }
}

function syncCheckoutForms(): void {
  const stableID = readStableId();
  if (!stableID) return;

  // O POST nativo também leva o atributo caso o update assíncrono esteja pendente.
  document.querySelectorAll<HTMLFormElement>('#cart, #CartDrawer-Form').forEach((form) => {
    let input = form.querySelector<HTMLInputElement>('input[name="attributes[stableID]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'attributes[stableID]';
      form.appendChild(input);
    }
    input.value = stableID;
  });
}

async function persistCartId(token: string): Promise<void> {
  if (token === _lastPersistedToken) return;

  // Seta antes do await para evitar race condition:
  // se um segundo evento chegar antes do fetch completar, cai no guard acima.
  _lastPersistedToken = token;
  console.debug(`[cart-attribute-tracker] persist cartId: ${token}`);

  try {
    await silentCartUpdate({ attributes: { _cartId: token } });
  } catch (err) {
    // Reverte em caso de erro para permitir nova tentativa
    _lastPersistedToken = null;
    console.warn('[cart-attribute-tracker] Failed to persist cartId:', err);
  }
}

document.addEventListener(INSIDER_CART_CHANGED, (e: Event) => {
  const { cart } = (e as CustomEvent<CartChangedDetail>).detail;
  if (cart?.token) {
    _currentToken = cart.token;
    persistCartId(cart.token);
    const stableID = readStableId();
    if (stableID && cart.attributes?.stableID === stableID) {
      _lastPersistedStableKey = JSON.stringify([cart.token, stableID]);
    } else {
      persistStableId();
    }
  }
});

window.addEventListener('statsigReady', (e: Event) => {
  if (!(e as CustomEvent<{ ready: boolean }>).detail?.ready) return;
  syncCheckoutForms();
  persistStableId();
});

// Capture cobre formulários recriados pelo drawer e handlers que chamam form.submit().
document.addEventListener('click', (e: MouseEvent) => {
  if (e.target instanceof Element && e.target.closest('[name="checkout"]')) {
    syncCheckoutForms();
  }
}, true);
document.addEventListener('submit', (e: Event) => {
  if (e.target instanceof HTMLFormElement && ['cart', 'CartDrawer-Form'].includes(e.target.id)) {
    syncCheckoutForms();
  }
}, true);

// Em visitas seguintes o ID já pode existir antes do evento statsigReady.
syncCheckoutForms();
persistStableId();
